import { IconEye, IconEyeOff } from "@tabler/icons-react";
import React, { useState } from "react";
import { UseFormRegister, FieldError, RegisterOptions } from "react-hook-form";
import FormErrorMessage from "./common/FormErrorMessage";

interface InputProps {
  name: string;
  type?: string;
  title?: string;
  placeholder?: string;
  register?: UseFormRegister<any>;
  validation?: RegisterOptions;
  error?: FieldError | any;
  other?: any;
  className?: string;
  labelClass?: string;
  disabled?: boolean;
  defaultValue?: string;
  value?: string | number;
  removeDiv?: boolean;
  step?: any;
  inputMode?: any;
  inputClassName?: any;
  pattern?: string;
  min?: any;
  /** When true, the error message span is NOT rendered — the parent is responsible for showing it.
   *  Use this for inputs embedded inside custom wrappers (e.g. dial-code mobile inputs). */
  hideError?: boolean;
}

const Input: React.FC<InputProps> = ({
  name,
  title,
  type,
  placeholder = "",
  register,
  validation,
  error,
  other,
  className = "",
  labelClass = "",
  removeDiv = false,
  disabled = false,
  defaultValue = "",
  value,
  step,
  inputMode,
  inputClassName,
  pattern,
  min,
  hideError = false,
}) => {
  const [showPassword, setShowPassword] = useState(false);

  // When no custom inputClassName is given, we use the default style.
  // The border turns red when there is a validation error.
  const defaultInputClassName = `w-full rounded-full py-[6px] px-4 outline-none bg-white border ${
    error ? "border-red-500" : "border-[#D8D8D8]"
  } text-[#1E1E1E] ${type === "number" ? "hide-spinner" : ""}`;

  const inputField = (
    <input
      type={type === "password" && showPassword ? "text" : type}
      pattern={pattern}
      placeholder={placeholder}
      className={inputClassName ? inputClassName : defaultInputClassName}
      {...(value !== undefined ? { value } : { defaultValue })}
      {...(register ? register(name, validation) : {})}
      {...other}
      disabled={disabled}
      {...(step !== undefined ? { step } : {})}
      {...(inputMode !== undefined ? { inputMode } : {})}
      autoComplete="off"
      onKeyDown={(e) => {
        if (type === "number" && ["e", "E", "+", "-"].includes(e.key)) {
          e.preventDefault();
        }
      }}
      min={min}
    />
  );

  return (
    <div className={`space-y-1 ${className}`}>
      {title && (
        <label
          className={
            labelClass ? labelClass : "text-[#1E293B] text-sm whitespace-nowrap"
          }
        >
          {title}
          {validation?.required && <span className="text-red-600 ml-1">*</span>}
        </label>
      )}
      <div className="w-full">
        {!removeDiv ? (
          <div className="relative">
            {inputField}
            {type === "password" && (
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/4 text-gray-500"
              >
                {showPassword ? (
                  <IconEyeOff size={18} />
                ) : (
                  <IconEye size={18} />
                )}
              </button>
            )}
          </div>
        ) : (
          inputField
        )}

        {/* Error message — always shown unless hideError is true */}
        {!hideError && <FormErrorMessage error={error} />}
      </div>
    </div>
  );
};

export default Input;
